import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsPropertiesOpen(false);
      }
    };

    if (isPropertiesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPropertiesOpen]);

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const propertyCategories = [
    { name: 'Todas las Propiedades', path: '/propiedades', category: 'all' },
    { name: 'Residenciales', path: '/propiedades?category=residential', category: 'residential' },
    { name: 'Comerciales', path: '/propiedades?category=commercial', category: 'commercial' },
    { name: 'Terrenos', path: '/propiedades?category=land', category: 'land' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center group">
            <img 
              src="/gudo-logo-header.png" 
              alt="GUDO Real Estate" 
              className="h-12 w-auto md:h-14 transition-transform group-hover:scale-105"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-golden-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Propiedades Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsPropertiesOpen(!isPropertiesOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-1 ${
                  location.pathname === '/propiedades'
                    ? 'bg-golden-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                aria-expanded={isPropertiesOpen}
                aria-haspopup="true"
              >
                <span>Propiedades</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPropertiesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isPropertiesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  {propertyCategories.map((cat) => (
                    <Link
                      key={cat.path}
                      to={cat.path}
                      onClick={() => setIsPropertiesOpen(false)}
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-golden-50 hover:text-golden-600 transition-colors font-medium"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:+525649796366" className="flex items-center space-x-2 text-sm text-gray-600 hover:text-golden-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span>+52 564 979 6366</span>
            </a>
            <Button asChild className="bg-golden-600 hover:bg-golden-700 text-white">
              <Link to="/contacto">Agendar Cita</Link>
            </Button>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-golden-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Propiedades Mobile */}
            <div className="space-y-1">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                Propiedades
              </div>
              {propertyCategories.map((cat) => (
                <Link
                  key={cat.path}
                  to={cat.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-golden-50 hover:text-golden-600 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <a href="tel:+525649796366" className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                <span>+52 564 979 6366</span>
              </a>
              <Button asChild className="w-full mt-2 bg-golden-600 hover:bg-golden-700 text-white">
                <Link to="/contacto">Agendar Cita</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
